/*!
 * @copyright
 * Copyright(c) 2019 SICK AG
 */

import { Route } from './Route';
import { Solids, Stage, Materials } from '@sick-davinci/glow';
import { TextureLoader, Vector2, MixOperation, DoubleSide, Mesh, Line, Euler, Group } from 'three';

export class DrawHelper {

  constructor(private _stage: Stage, public height: number, public width: number, private uniforms: any) {
    window['DrawHelperUniforms'] = uniforms;
  }

  createWall(x1: number, y1: number, x2: number, y2: number) {
    const texture = new TextureLoader().load('./resources/Imported_Wood_Cherry_Original_4288173610__45dd67ec_9860_dc4e_bfbe_6e29412ddf74_.jpg');

    const v1 = new Vector2(x1, y1);
    const v2 = new Vector2(x2, y2);
    const dir = v2.clone().sub(v1);
    const len = dir.length();
    const norm = dir.normalize();
    const mid = new Vector2((v1.x + v2.x) / 2, (v1.y + v2.y) / 2);
    const rot = Math.atan2(norm.x, norm.y);

    this._stage.applyThreeDirect((scene) => {
      const material = Materials.create({ type: 'standard', params: { map: texture } }, scene);
      const solid = new Mesh(Solids.createBox(this.width, this.height, len), material);

      solid.quaternion.setFromEuler(new Euler(0, rot, 0));
      solid.position.set(mid.x, 0, mid.y);
      solid.castShadow = true;
      solid.receiveShadow = true;

      scene.add(solid);
      scene.setTag('level', solid);
    });
  }

  createPlank(x1: number, y1: number, x2: number, y2: number) {
    const texture = new TextureLoader().load('./resources/wood.jpg');
    const v1 = new Vector2(x1, y1);
    const v2 = new Vector2(x2, y2);
    const dir = v2.clone().sub(v1);
    const len = dir.length();
    const norm = dir.normalize();
    const mid = new Vector2((v1.x + v2.x) / 2, (v1.y + v2.y) / 2);
    const rot = Math.atan2(norm.x, norm.y);

    this._stage.applyThreeDirect((scene) => {
      const material = Materials.create({ type: 'standard', params: { map: texture } }, scene);
      const solid = new Mesh(Solids.createBox(this.width, this.height + 1, len), material);

      solid.position.set(mid.x, 0, mid.y);
      solid.quaternion.setFromEuler(new Euler(0, rot, 0));
      solid.castShadow = true;
      solid.receiveShadow = true;
      solid.name = 'plank';

      scene.add(solid);
      scene.setTag('level', solid);
    });

  }

  createPillar(x1: number, y1: number) {
    const texture = new TextureLoader().load('./resources/Imported_Wood_Cherry_Original_4288173610__45dd67ec_9860_dc4e_bfbe_6e29412ddf74_.jpg');

    this._stage.applyThreeDirect((scene) => {
      const material = Materials.create({ type: 'standard', params: { map: texture } }, scene);
      const solid = new Mesh(Solids.createCylinder(this.width / 2, this.width / 2, this.height), material);

      solid.position.set(x1, 0, y1);
      solid.castShadow = true;
      solid.receiveShadow = true;

      scene.add(solid);
      scene.setTag('level', solid);
    });

  }

  createGround(w: number, h: number) {
    const texture = new TextureLoader().load('./resources/drawing.jpg');
    this._stage.applyThreeDirect((scene) => {
      const material = Materials.create({ type: 'standard', params: { color: 0xffffff, map: texture } }, scene);
      material.side = DoubleSide;
      const solid = new Mesh(Solids.createRectangle(w, h), material);
      solid.position.set(w / 2, -this.height / 2, h / 2);
      solid.quaternion.setFromEuler(new Euler(Math.PI / 2, 0, 0));
      solid.castShadow = false;
      solid.receiveShadow = true;
      solid.name = 'ground';

      scene.set(solid);
      scene.setTag('level', solid);
    });
  }

  createBall(x1: number, y1: number, radius: number) {
    this._stage.applyThreeDirect((scene) => {
      const material = Materials.create({ type: 'lambert', useBackground: true, params: { color: 0x0000ff, combine: MixOperation, reflectivity: 0.3 } }, scene);
      material.side = DoubleSide;
      const solid = new Mesh(Solids.createSphere(radius), material);
      solid.position.set(x1, 0, y1);
      solid.castShadow = true;
      solid.receiveShadow = false;
      solid.name = 'ball';

      scene.set(solid);
      scene.setTag('level', solid);
    });
  }

  createGoal(x1: number, y1: number, radius: number) {
    this._stage.applyThreeDirect((scene) => {
      const material = Materials.create(
        {
          type: 'shader', params: {
            transparent: true,
            uniforms: this.uniforms,
            vertexShader: ` varying vec2 vUv;
                                      void main()
                                      {
                                        vUv = uv;
                                        vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                                        gl_Position = projectionMatrix * mvPosition;
                                      }`,
            fragmentShader: ` uniform float time;
                                        varying vec2 vUv;
                                        void main( void ) {
                                          float x = (vUv.x-0.5)*2.0;
                                          float y = (vUv.y-0.5)*2.0;
                                          float len = sqrt(x*x+y*y);
                                          float alpha = sqrt(1.0-len);
                                          float angle = atan(x,y)*3.0;
                                          float r = sin(angle+len*19.0+time*0.25);
                                          float g = sin(angle+len*8.0+time*0.375);
                                          float b = sin(angle+len*7.0+time*0.5);
                                          gl_FragColor = vec4( 0.0, (r+b)*0.3, (g+r)*0.4 , alpha );
                                          //gl_FragColor = vec4( 0, 0, g , alpha  );
                                        }`,
          },
          updater: 'DrawHelperUniforms.time.value += 0.05',
        }
        , scene);
      material.side = DoubleSide;
      const solid = new Mesh(Solids.createCircle(radius), material);
      solid.position.set(x1, 0.1 - this.height / 2, y1);
      solid.quaternion.setFromEuler(new Euler(-Math.PI / 2, 0, 0));
      solid.castShadow = true;
      solid.receiveShadow = false;
      solid.name = 'goal';

      scene.set(solid);
      scene.setTag('level', solid);
    });
  }

  createDirection(x: number, y: number, dx: number, dy: number, r: number) {
    this._stage.applyThreeDirect((scene) => {
      const material = Materials.create({ type: 'line' }, scene);
      material.side = DoubleSide;
      const solid = new Line(Solids.createLineSegment(x, y, 0, x + dx * 3 * r, y + dy * 3 * r, 0), material);
      solid.quaternion.setFromEuler(new Euler(Math.PI / 2, 0, 0));
      solid.castShadow = false;
      solid.receiveShadow = true;
      solid.name = 'dir';

      scene.set(solid);
      scene.setTag('level', solid);
    });
  }

  createRoute(route: Route) {

    const points = route.getPoints();

    this._stage.applyThreeDirect((scene) => {

      if (points.length > 0) {
        let x1 = points[0].x;
        let y1 = points[0].y;
        let x2 = 0;
        let y2 = 0;
        const solids = new Group();
        solids.name = 'route';
        for (let i = 1; i < points.length; i++) {
          x2 = points[i].x;
          y2 = points[i].y;

          const material = Materials.create({ type: 'line' }, scene);
          material.side = DoubleSide;
          const solid = new Line(Solids.createLineSegment(x1, y1, 0, x2, y2, 0), material);
          solid.quaternion.setFromEuler(new Euler(Math.PI / 2, 0, 0));
          solid.castShadow = false;
          solid.receiveShadow = true;
          solids.add(solid);

          x1 = x2;
          y1 = y2;
        }

        scene.set(solids);
      } else {
        scene.remove(scene.getObjectByName('route'));
      }
    });
  }
}
